import * as stylex from '@stylexjs/stylex';
import { clsx } from 'clsx';
import { colors } from '../theme/colors.stylex';
import type { LoadingIndicatorProps } from './LoadingIndicator.types';

const spin = stylex.keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
});

const styles = stylex.create({
  spinner: {
    'position': 'relative',
    'display': 'inline-block',
    'flex': '0 0 auto',
    'width': 40,
    'height': 40,
    'color': colors.accent,
    'lineHeight': 1,
    'verticalAlign': 'middle',
    'borderRadius': '50%',
    'backgroundImage': 'conic-gradient(from 0deg, transparent, currentColor)',
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 7px), black calc(100% - 7px + 0.5px))',
    'animationName': spin,
    'animationDuration': '1s',
    'animationTimingFunction': 'linear',
    'animationIterationCount': 'infinite',
    '::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: '50%',
      width: 7,
      height: 7,
      backgroundColor: 'currentColor',
      borderRadius: '50%',
      transform: 'translateX(-50%)',
    },
  },
  customSize: (size: number) => ({ width: size, height: size }),
  customThickness: (thickness: number) => ({
    'maskImage': `radial-gradient(farthest-side, transparent calc(100% - ${thickness}px), black calc(100% - ${thickness}px + 0.5px))`,
    '::before': { width: thickness, height: thickness },
  }),
  customColor: (color: string) => ({ color }),
});

const sizeStyles = stylex.create({
  xs: {
    'width': 12,
    'height': 12,
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px + 0.5px))',
    '::before': { width: 2, height: 2 },
  },
  sm: {
    'width': 16,
    'height': 16,
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px + 0.5px))',
    '::before': { width: 3, height: 3 },
  },
  md: {
    'width': 24,
    'height': 24,
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 4px), black calc(100% - 4px + 0.5px))',
    '::before': { width: 4, height: 4 },
  },
  lg: {
    'width': 40,
    'height': 40,
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 7px), black calc(100% - 7px + 0.5px))',
    '::before': { width: 7, height: 7 },
  },
  xl: {
    'width': 64,
    'height': 64,
    'maskImage': 'radial-gradient(farthest-side, transparent calc(100% - 10px), black calc(100% - 10px + 0.5px))',
    '::before': { width: 10, height: 10 },
  },
});

/**
 * Shared loading spinner that inherits the active Seeq theme color unless a color is provided.
 */
const LoadingIndicator = ({
  id,
  customId,
  testId,
  size,
  thickness,
  color,
  extraClassNames,
  xstyle,
  style,
  ariaLabel,
  ariaHidden,
  role,
  onClick,
  title,
}: LoadingIndicatorProps) => {
  const isDecorative = !ariaLabel && !role && !onClick;
  const styleProps = stylex.props(
    styles.spinner,
    typeof size === 'string' && sizeStyles[size],
    typeof size === 'number' && styles.customSize(size),
    typeof size === 'number' && styles.customThickness(Math.max(2, Math.round(size / 6))),
    thickness !== undefined && styles.customThickness(thickness),
    color ? styles.customColor(color) : undefined,
    xstyle,
  );

  return (
    <div
      id={id}
      data-customid={customId}
      data-testid={testId}
      aria-hidden={ariaHidden ?? (isDecorative ? true : undefined)}
      aria-label={ariaLabel}
      role={role}
      title={title}
      className={clsx('loading-spinner', styleProps.className, extraClassNames)}
      style={{ ...styleProps.style, ...style }}
      onClick={onClick}
    />
  );
};

export default LoadingIndicator;
